<template>
  <FormContainer @submitForm="saveContributoFondoPerduto">
    <div class="row">
      <FormInputTextWidget
        class="col-lg-4 col-sm-6"
        label="Nome del contributo a fondo perduto"
        v-model="contributoFondoPerduto.name"
        :errorMsg="errorHash['name']"
        @input="removeError('name')"
      />

      <FormInputAmountWidget
        class="col-lg-4 col-sm-6"
        label="Importo"
        v-model.number="contributoFondoPerduto.importo"
        :min="0"
        :errorMsg="errorHash['importo']"
        @input="removeError('importo')"
      />

      <FormInputSelectWidget
        class="col-lg-4 col-sm-6"
        label="Tipologia"
        v-model="contributoFondoPerduto.tipoId"
        :options="tipoOptions"
        :errorMsg="errorHash['tipo']"
        @input="removeError('tipo')"
      />

      <FormInputMonthPickerWidget
        class="col-lg-4 col-sm-6"
        label="Mese di concessione"
        v-model="contributoFondoPerduto.concessione"
        :errorMsg="errorHash['concessione']"
        @input="removeError('concessione')"
      />

      <FormInputIntegerWidget
        v-if="contributoFondoPerduto.tipoId === 2"
        class="col-lg-4 col-sm-6"
        label="Mesi di ammortamento medio dei cespiti agevolati"
        v-model.number="contributoFondoPerduto.nbMesiAmmortamentoMedioCespitiAgevolati"
        :errorMsg="errorHash['nbMesiAmmortamentoMedioCespitiAgevolati']"
        @input="removeError('nbMesiAmmortamentoMedioCespitiAgevolati')"
      />

      <FormInputMonthPickerWidget
        v-if="contributoFondoPerduto.tipoId === 2"
        not-required
        class="col-lg-4 col-sm-6"
        label="Mese di investimento"
        v-model="contributoFondoPerduto.investimento"
        :errorMsg="errorHash['investimento']"
        @input="removeError('investimento')"
      />
    </div>

    <div class="row">
      <div class="col-12 col-xl-8 mt-4">
        <FormInputPlanWidget
          label="Piano delle erogazioni"
          v-model="pianoErogazioni"
          :total="contributoFondoPerduto.importo"
          :first-month="contributoFondoPerduto.concessione"
          :errorMsg="errorHash['pianoErogazioni']"
          @input="removeError('pianoErogazioni')"
        />
      </div>
    </div>

    <div class="row">
      <div class="col-12 mt-4 text-center text-sm-right">
        <button class="btn btn-primary" type="submit">
          <i class="fas fa-save mr-2"></i>
          Salva contributo a fondo perduto
        </button>
      </div>
    </div>
  </FormContainer>
</template>

<script>

import validateMixin from '@/libs/Form/mixins/validate.js';
import { arrayPlanToObjectPlan, objectPlanToArrayPlan, thereAreEmptyMonths, getPlanTotal } from '@/helpers/plans.js';

export default {
  mixins: [validateMixin],
  components: {
    FormContainer: () => import('@/libs/Form/components/FormContainer.vue'),
    FormInputTextWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputTextWidget.vue'),
    FormInputIntegerWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputIntegerWidget.vue'),
    FormInputAmountWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputAmountWidget.vue'),
    FormInputPlanWidget: () => import('@/views/components/Form/FormInputPlanWidget.vue'),
    FormInputSelectWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputSelectWidget.vue'),
    FormInputMonthPickerWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputMonthPickerWidget.vue'),
  },
  props: {
    contributoFondoPerduto: {
      type: Object,
      default: () => ({
        name: null,
        tipoId: null,
        importo: null,
        concessione: null,
        nbMesiAmmortamentoMedioCespitiAgevolati: 60,
        pianoErogazioni: null,
        investimento: null,
      }),
    },
    tipoOptions: {
      type: Array,
      default: () => [
        { value: 1, label: 'Conto esercizio' },
        { value: 2, label: 'Conto capitale' },
      ],
    },
    edit: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    pianoErogazioni: {
      get () {
        return objectPlanToArrayPlan(this.contributoFondoPerduto.pianoErogazioni);
      },
      set (newVal) {
        this.contributoFondoPerduto.pianoErogazioni = arrayPlanToObjectPlan(newVal);
      },
    },
    pianoErogazioniTotal () {
      return getPlanTotal(this.pianoErogazioni);
    },
  },
  methods: {
    validate () {
      this.clearErrors();

      if (!this.contributoFondoPerduto.name) {
        this.addError('Il nome del contributo a fondo perduto è obbligatorio', 'name');
      }

      if (!this.contributoFondoPerduto.tipoId) {
        this.addError('Il tipo del contributo a fondo perduto è obbligatorio', 'tipo');
      }

      if (!this.contributoFondoPerduto.importo) {
        this.addError('L\'importo del contributo a fondo perduto è obbligatorio', 'importo');
      }

      if (!this.contributoFondoPerduto.concessione) {
        this.addError('Il mese di concessione del contributo a fondo perduto è obbligatorio', 'concessione');
      }

      if (this.contributoFondoPerduto.tipoId === 2 && !this.contributoFondoPerduto.nbMesiAmmortamentoMedioCespitiAgevolati) {
        this.addError('Il numero di mesi di ammortamento medio dei cespiti agevolati è obbligatorio', 'nbMesiAmmortamentoMedioCespitiAgevolati');
      }

      if (this.contributoFondoPerduto.importo && this.contributoFondoPerduto.concessione) {
        if (!this.contributoFondoPerduto.pianoErogazioni) {
          this.addError('Inserire il piano delle erogazioni', 'pianoErogazioni');
        } else if (this.pianoErogazioniTotal > this.contributoFondoPerduto.importo) {
          this.addError('Eccesso di erogazione scadenziato', 'pianoErogazioni');
        } else if (this.pianoErogazioniTotal < this.contributoFondoPerduto.importo) {
          this.addError('Completare il piano delle erogazioni', 'pianoErogazioni');
        } else if (thereAreEmptyMonths(this.pianoErogazioni)) {
          this.addError('Completare il piano delle erogazioni', 'pianoErogazioni');
        }
      }

      return !this.hasErrors;
    },
    saveContributoFondoPerduto () {
      if (!this.validate()) {
        return;
      }

      this.$emit('saved', this.contributoFondoPerduto);
    },
  },
};

</script>
